import { useState } from 'react';
import type { ConsentKind, ConsentTemplate, UpsertConsentTemplate } from '@salonomy/types';
import { Button, Field, Modal, Select, TextArea, TextInput } from '@salonomy/ui';
import { useUpsertConsentTemplate } from '../../data/consent.js';
import { useToast } from '../../lib/toast.js';
import { CONSENT_KIND_LABEL, CONSENT_KINDS } from '../clients/consent-shared.js';

/**
 * Create / version a consent template (manager). A POST creates v1; a PATCH that changes the WORDING
 * (title/body/fields/validityDays) creates a NEW version server-side — a row with signed records is never
 * mutated, so a later edit can never retro-alter signed evidence. We make that contract explicit in the
 * copy when editing, so a manager understands editing the wording forks a new version.
 *
 * `fields` (the structured form-field schema) is authored as raw JSON here — the capture flow best-effort
 * renders an array of `{ key, label, type }` descriptors. Leaving it blank is fine (free-text capture).
 */
export function ConsentTemplateModal({
  existing,
  onClose,
}: {
  existing?: ConsentTemplate;
  onClose: () => void;
}) {
  const upsert = useUpsertConsentTemplate();
  const toast = useToast();
  const editing = Boolean(existing);

  const [kind, setKind] = useState<ConsentKind>(existing?.kind ?? 'consultation');
  const [title, setTitle] = useState(existing?.title ?? '');
  const [body, setBody] = useState(existing?.body ?? '');
  const [validityDays, setValidityDays] = useState(
    existing?.validityDays != null ? String(existing.validityDays) : '',
  );
  const [fieldsJson, setFieldsJson] = useState(
    existing?.fields != null ? JSON.stringify(existing.fields, null, 2) : '',
  );
  const [busy, setBusy] = useState(false);

  const isDated = kind === 'patch_test' || kind === 'allergy';

  async function submit() {
    if (!title.trim()) {
      toast.error('Give the form a title.');
      return;
    }

    let parsedFields: unknown;
    if (fieldsJson.trim()) {
      try {
        parsedFields = JSON.parse(fieldsJson);
      } catch {
        toast.error('The form fields must be valid JSON (or leave it blank).');
        return;
      }
    }

    let validity: number | null = null;
    if (isDated && validityDays.trim()) {
      const n = Number(validityDays);
      if (!Number.isInteger(n) || n < 1 || n > 3650) {
        toast.error('Validity must be a whole number of days between 1 and 3650.');
        return;
      }
      validity = n;
    }

    const payload: UpsertConsentTemplate = {
      kind,
      title: title.trim(),
      body: body,
      validityDays: isDated ? validity : null,
      ...(fieldsJson.trim() ? { fields: parsedFields } : { fields: null }),
    };

    setBusy(true);
    try {
      await upsert.mutateAsync({ id: existing?.id, body: payload });
      toast.success(editing ? 'New version saved' : 'Consent form created');
      onClose();
    } catch {
      toast.error('Could not save this consent form.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? `Edit “${existing!.title}”` : 'New consent form'}
      width={560}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {editing ? 'Save new version' : 'Create form'}
          </Button>
        </>
      }
    >
      {editing ? (
        <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 14 }}>
          Editing the wording creates a <strong>new version</strong>. Records already signed against
          the current version keep their frozen copy — a change can never alter signed evidence.
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Kind">
            <Select value={kind} onChange={(e) => setKind(e.target.value as ConsentKind)}>
              {CONSENT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {CONSENT_KIND_LABEL[k]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Title">
            <TextInput
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Colour consultation"
            />
          </Field>
        </div>

        {isDated ? (
          <Field
            label="Validity (days)"
            hint="How long a signed record stays valid. Blank uses the service’s patch-test window."
          >
            <TextInput
              inputMode="numeric"
              value={validityDays}
              onChange={(e) => setValidityDays(e.target.value)}
              placeholder="e.g. 180"
              style={{ width: 160 }}
            />
          </Field>
        ) : null}

        <Field
          label="Wording (the liability surface)"
          hint="The prose the client agrees to. Frozen verbatim into every record signed against this version."
        >
          <TextArea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            style={{ minHeight: 160 }}
          />
        </Field>

        <Field
          label="Form fields (JSON, optional)"
          hint='Array of { "key", "label", "type": "text" | "textarea" | "boolean" }. Leave blank for free-text capture.'
        >
          <TextArea
            value={fieldsJson}
            onChange={(e) => setFieldsJson(e.target.value)}
            className="mono"
            style={{ minHeight: 120 }}
            placeholder='[{ "key": "allergies", "label": "Any known allergies?", "type": "textarea" }]'
          />
        </Field>
      </div>
    </Modal>
  );
}
